import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState, useContext, useCallback, useEffect } from "react";
import BookingProcess from "../../components/TourView/BookingProcess";
import PackageItinerary from "../../components/PackageItinerary";
import { Overlay } from "../../components/common/product/misc";
import TourViewMap from "../../components/TourViewMap";
import { createUseStyles } from "react-jss";
import EventDetails from "../../components/TourView/EventDetails";
import ContactUs from "../../components/common/product/ContactUs";
import AdditionalInfo from "../../components/common/product/AdditionalInfo";
import InclusionsExclusions from "../../components/common/product/InclusionsExclusions";
import Description from "../../components/common/product/Description";
import Highlights from "../../components/common/product/Highlights";
import Header from "../../components/common/product/Header";
import { StyleTypeContext } from "../../umbilical_tools";
import { cStyler } from "../../helpers";
import { useSelector, useDispatch } from "react-redux";
import { dayByDayDataSelector } from "../../umbilical_hocs";
import FullScreenLoader from "../../components/widgets/FullScreenLoader";
import { getAddonDetails } from "../../actions/init";
import jssVariables from "../../jss_setup";

const packageStyles = createUseStyles({
  view: (props) => ({
    position: "relative",
    background: jssVariables.colors.background.light,
    height: props.loading ? "100vh" : "auto",
    ..._.get(props, "customStyles.view", {}),
  }),
  description_highlights: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} * 3)`,
    gridTemplateColumns: "1fr",
    padding: "5vw",
    paddingTop: "10vw",
    ..._.get(props, "customStyles.description_highlights", {}),
  }),
  videoContainer: (props) => ({
    marginTop: "5vh",
    ..._.get(props, "customStyles.videoContainer", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    description_highlights: (props) => ({
      gridTemplateColumns: props.hasHighlights ? "2fr 1fr" : "1fr",
      padding: jssVariables.spacing.center_padding_large,
      marginTop: "2vw",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.description_highlights.smallscreen", {}),
    }),
    videoContainer: (props) => ({
      marginTop: "5vh",
      ..._.get(props, "customStyles.videoContainer.smallscreen", {}),
    }),
  },
});
const PackageDetails = ({ tourReference, productType }) => {
  const [showForm, setShowForm] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [dayIdx, setDayIdx] = useState(0);
  const [uid, setUid] = useState("");
  const [srvType, setSrvType] = useState("");

  const { type, brand } = useContext(StyleTypeContext);
  const dispatch = useDispatch();

  const {
    loading,
    email,
    details,
    price_info,
    isMarketplace,
    source_entity,
    step: bookingStep,
  } = useSelector((state) => {
    return {
      loading: _.get(state.TourDetails, "loading", true),
      email: _.get(
        state.WebsiteMetadata,
        "metadata.contact_information.email",
        ""
      ),
      details: _.get(state.TourDetails, "details", {}),
      price_info: _.get(state.TourPriceInfo, "price_info", {}),
      adults: _.get(state.PriceSearchForm, "adults", 0),
      date: _.get(state.PriceSearchForm, "date", ""),
      source_entity: _.get(state.WebsiteMetadata, "metadata.source_entity", ""),
      isMarketplace: _.get(
        state.WebsiteMetadata,
        "metadata.marketplace",
        false
      ),
      step: _.get(state.BookingInformation, "step", 0),
    };
  });

  const getDetails = useCallback(
    (reference, product_type, source_entity) =>
      dispatch(getAddonDetails(reference, product_type, source_entity)),
    [dispatch]
  );

  useEffect(() => {
    if (source_entity) {
      getDetails(tourReference, productType, source_entity);
    }
  }, [source_entity, tourReference]);

  const dayByDayData = useSelector((state) => dayByDayDataSelector(state));

  const showOverlay = showForm || !_.isEmpty(price_info);

  const hasHighlights = _.get(details, "highlights", []).length > 0;
  const classes = packageStyles({
    loading,
    hasHighlights,
    customStyles: cStyler({
      type,
      brand,
      objName: "packageViewStyles",
    }),
  });

  return showEventDetails ? (
    <EventDetails
      dayByDayData={dayByDayData}
      dayIdx={dayIdx}
      uid={uid}
      srvType={srvType}
      handleHideEventDetails={() => setShowEventDetails(false)}
    />
  ) : bookingStep > 0 ? (
    <React.Fragment>
      <Overlay />
      <BookingProcess
        background={jssVariables.colors.background.dark}
        baseColor={jssVariables.colors.base_color}
      />
    </React.Fragment>
  ) : (
    <div className={classes.view}>
      {showOverlay ? <Overlay /> : null}
      {loading ? (
        <FullScreenLoader />
      ) : (
        <React.Fragment>
          <Header
            showForm={showForm}
            details={details}
            isMarketplace={isMarketplace}
            handleShowForm={() => setShowForm(true)}
            customStyles={cStyler({
              type,
              brand,
              objName: "packageViewStyles",
            })}
          />
          <div className={classes.description_highlights}>
            <Description
              details={details}
              customStyles={cStyler({
                type,
                brand,
                objName: "packageViewStyles",
              })}
            />
            <Highlights
              highlights={_.get(details, "highlights", [])}
              customStyles={cStyler({
                type,
                brand,
                objName: "packageAddOnItineraryStyles",
              })}
            />
          </div>
          <PackageItinerary
            details={details}
            dayByDayData={dayByDayData}
            onViewDetails={(dayIdx, uid, srvType) => {
              setSrvType(srvType);
              setUid(uid);
              setDayIdx(dayIdx);
              setShowEventDetails(true);
            }}
          />
          <InclusionsExclusions
            details={details}
            customStyles={cStyler({
              type,
              brand,
              objName: "packageAddOnItineraryStyles",
            })}
          />
          {type === "type2" && (
            <AdditionalInfo
              details={details}
              customStyles={cStyler({
                type,
                brand,
                objName: "packageViewStyles",
              })}
            />
          )}
          <TourViewMap
            mapType="pkg-map"
            destinations={_.get(details, "destinations", [])}
            dayByDayData={dayByDayData}
          />
          {type !== "type2" && (
            <AdditionalInfo
              details={details}
              customStyles={cStyler({
                type,
                brand,
                objName: "packageViewStyles",
              })}
            />
          )}
          {type !== "type2" && <ContactUs contactEmail={email} />}
        </React.Fragment>
      )}
    </div>
  );
};
PackageDetails.propTypes = {
  tourReference: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
};
export default PackageDetails;
